import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFormState, useWatch } from "react-hook-form";
import { Trash, Plus, Lock, Unlock } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./components/ui/form";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import React, { useEffect, useMemo, useState } from "react";
import { match, P } from "ts-pattern";

import { type OnChangeFn, type RowSelectionState } from "@tanstack/react-table";

import { calculateMortgageCostPerYear } from "./lib/calculators";
import { EditableDataTable } from "./components/EditableDataTable";
import { Checkbox } from "./components/ui/checkbox";
import { DialogDrawer } from "./components/DialogDrawer";
import {
  defaultMortgageData,
  FrequencyTypeArr,
  initialMortgageData,
  MortgageDataSchema,
} from "./data";
import type {
  CreateHandlerFlat,
  DeleteHandlerFlat,
  UpdateHandlerFlat,
} from "./hooks/useCrudReducer";
import {
  useEditableTable,
  type EditableColumnDef,
} from "./hooks/useEditableTable";
import { ResetButton } from "./components/ResetButton";
import { SubSectionCard } from "./components/SubSectionCard";
import { Switch } from "./components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";

const mortgageDatacolumns: EditableColumnDef<MortgageDataSchema>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    meta: {
      editable: false,
    },
  },
  {
    accessorKey: "propertyValue",
    header: "Propety Value ($)",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "depositAmount",
    header: "Deposit Amount ($)",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "loanAmount",
    header: "Loan Amount ($)",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "interestRate",
    header: "Interest Rate (%)",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "loanTerm",
    header: "Loan Term (Yrs)",
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "interestOnly",
    header: "Interest Only",
    cell: ({ cell }) => {
      const value = cell.getValue();
      return value === true ? "Yes" : "No";
    },
    meta: {
      editable: true,
    },
  },
  {
    accessorKey: "repaymentFrequency",
    header: "Repayment Frequency",
    meta: {
      editable: true,
    },
  },
  {
    id: "actions",
    cell: ({ table, row }) => (
      <Button
        variant="ghost"
        className="h-8 w-8 p-0"
        onClick={() => table.options.meta!.deleteRow(row)}
      >
        <span className="sr-only">Remove</span>
        <Trash className="h-4 w-4" />
      </Button>
    ),
    meta: {
      editable: false,
    },
  },
];

interface MortgageFormProps {
  initialValues: MortgageDataSchema;
  onFormSubmit: (values: MortgageDataSchema) => void;
  onFormCancel: () => void;
}

function MortgageForm({
  initialValues,
  onFormSubmit,
  onFormCancel,
}: MortgageFormProps) {
  const form = useForm<MortgageDataSchema>({
    resolver: zodResolver(MortgageDataSchema),
    values: initialValues,
  });

  function onSubmitHandler(values: MortgageDataSchema) {
    onFormSubmit(values);
  }

  function onCancelHandler() {
    form.reset();
    onFormCancel();
  }

  const numToInputString = (num: number) => (num === 0 ? "" : num.toString());

  const mortgageCalculationFields = form.watch([
    "loanAmount",
    "loanTerm",
    "interestRate",
    "interestOnly",
    "repaymentFrequency",
  ]);
  const mortgageCostPerYear = useMemo(() => {
    const [
      loanAmount,
      loanTerm,
      interestRate,
      interestOnly,
      repaymentFrequency,
    ] = mortgageCalculationFields;
    const repayment = calculateMortgageCostPerYear({
      loanAmount,
      loanTerm,
      interestRate: interestRate / 100,
      interestOnly,
      repaymentFrequency,
    });
    return repayment;
  }, [mortgageCalculationFields]);

  const [lockedField, setLockedField] = useState<keyof MortgageDataSchema>(
    "loanToValuePercentage"
  );

  const calculateNewValues = (
    changedField: keyof MortgageDataSchema,
    values: MortgageDataSchema
  ) => {
    const { propertyValue, depositAmount, loanAmount, loanToValuePercentage } =
      values;

    let newLoanAmount = loanAmount;
    let newDepositAmount = depositAmount;
    let newLoanToValuePercentage = loanToValuePercentage;
    let newPropertyValue = propertyValue;

    match([lockedField, changedField])
      .with(
        [
          P.union("loanToValuePercentage", "depositAmount"),
          P.union("loanToValuePercentage", "depositAmount"),
        ],
        () => {
          // lvr = loanAmount / (depositAmount + loanAmount)
          const lvr = loanToValuePercentage / 100;
          newLoanAmount = (lvr * depositAmount) / (1 - lvr);
          newPropertyValue = depositAmount + newLoanAmount;
        }
      )
      .with(
        [
          P.union("loanToValuePercentage", "loanAmount"),
          P.union("loanToValuePercentage", "loanAmount"),
        ],
        () => {
          // lvr = loanAmount / (depositAmount + loanAmount)
          const lvr = loanToValuePercentage / 100;
          newDepositAmount = loanAmount / lvr - loanAmount;
          newPropertyValue = newDepositAmount + newLoanAmount;
        }
      )
      .with(
        [
          P.union("loanToValuePercentage", "propertyValue"),
          P.union("loanToValuePercentage", "propertyValue"),
        ],
        () => {
          // lvr = loanAmount / propertyValue
          const lvr = loanToValuePercentage / 100;
          newLoanAmount = lvr * propertyValue;
          newDepositAmount = propertyValue - newLoanAmount;
        }
      )
      .with(
        [
          P.union("depositAmount", "loanAmount"),
          P.union("depositAmount", "loanAmount"),
        ],
        () => {
          newPropertyValue = depositAmount + loanAmount;
          newLoanToValuePercentage = (loanAmount * 100) / newPropertyValue;
        }
      )
      .with(
        [
          P.union("depositAmount", "propertyValue"),
          P.union("depositAmount", "propertyValue"),
        ],
        () => {
          newLoanAmount = propertyValue - depositAmount;
          newLoanToValuePercentage = (newLoanAmount * 100) / propertyValue;
        }
      )
      .with(
        [
          P.union("loanAmount", "propertyValue"),
          P.union("loanAmount", "propertyValue"),
        ],
        () => {
          newLoanAmount = propertyValue - depositAmount;
          newLoanToValuePercentage = (newLoanAmount * 100) / propertyValue;
        }
      )
      .otherwise(() => {});

    return {
      propertyValue: +newPropertyValue.toFixed(2),
      loanAmount: +newLoanAmount.toFixed(2),
      depositAmount: +newDepositAmount.toFixed(2),
      loanToValuePercentage: +newLoanToValuePercentage.toFixed(2),
    };
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmitHandler)} className="space-y-8">
        <div className="grid grid-cols-2 gap-4 overflow-y-auto sm:max-h-[50vh] max-h-[40vh]">
          <FormField
            control={form.control}
            name="propertyValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Value</FormLabel>
                <FormControl>
                  <div className="flex flex-row gap-2 items-center">
                    <Input
                      type="number"
                      className="flex-1"
                      {...field}
                      value={numToInputString(field.value)}
                      onChange={(e) => {
                        const newValue = +e.target.value;
                        const newValues = calculateNewValues("propertyValue", {
                          ...form.getValues(),
                          propertyValue: newValue,
                        });
                        form.setValue("depositAmount", newValues.depositAmount);
                        form.setValue("loanAmount", newValues.loanAmount);
                        form.setValue(
                          "loanToValuePercentage",
                          newValues.loanToValuePercentage
                        );
                        return field.onChange(newValue);
                      }}
                    />
                    {lockedField !== "propertyValue" && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setLockedField("propertyValue");
                        }}
                      >
                        <span className="sr-only">"Lock"</span>
                        <Lock className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="depositAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Deposit Amount</FormLabel>
                <FormControl>
                  <div className="flex flex-row gap-2 items-center">
                    <Input
                      type="number"
                      className="flex-1"
                      {...field}
                      value={numToInputString(field.value)}
                      onChange={(e) => {
                        const newValue = +e.target.value;
                        const newValues = calculateNewValues("depositAmount", {
                          ...form.getValues(),
                          depositAmount: newValue,
                        });
                        form.setValue("propertyValue", newValues.propertyValue);
                        form.setValue("loanAmount", newValues.loanAmount);
                        form.setValue(
                          "loanToValuePercentage",
                          newValues.loanToValuePercentage
                        );
                        return field.onChange(newValue);
                      }}
                    />
                    {lockedField !== "depositAmount" && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setLockedField("depositAmount");
                        }}
                      >
                        <span className="sr-only">"Lock"</span>
                        <Lock className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="loanAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Loan Amount</FormLabel>
                <FormControl>
                  <div className="flex flex-row gap-2 items-center">
                    <Input
                      type="number"
                      className="flex-1"
                      {...field}
                      value={numToInputString(field.value)}
                      onChange={(e) => {
                        const newValue = +e.target.value;
                        const newValues = calculateNewValues("loanAmount", {
                          ...form.getValues(),
                          loanAmount: newValue,
                        });
                        form.setValue("depositAmount", newValues.depositAmount);
                        form.setValue("propertyValue", newValues.propertyValue);
                        form.setValue(
                          "loanToValuePercentage",
                          newValues.loanToValuePercentage
                        );
                        return field.onChange(newValue);
                      }}
                    />
                    {lockedField !== "loanAmount" && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setLockedField("loanAmount");
                        }}
                      >
                        <span className="sr-only">"Lock"</span>
                        <Lock className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="loanToValuePercentage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>LVR (%)</FormLabel>
                <FormControl>
                  <div className="flex flex-row gap-2 items-center">
                    <Input
                      type="number"
                      className="flex-1"
                      {...field}
                      value={numToInputString(field.value)}
                      onChange={(e) => {
                        const newValue = +e.target.value;
                        const newValues = calculateNewValues(
                          "loanToValuePercentage",
                          {
                            ...form.getValues(),
                            loanToValuePercentage: newValue,
                          }
                        );
                        form.setValue("depositAmount", newValues.depositAmount);
                        form.setValue("propertyValue", newValues.propertyValue);
                        form.setValue("loanAmount", newValues.loanAmount);
                        return field.onChange(newValue);
                      }}
                    />
                    {lockedField !== "loanToValuePercentage" && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setLockedField("loanToValuePercentage");
                        }}
                      >
                        <span className="sr-only">"Lock"</span>
                        <Lock className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="interestRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Interest</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={numToInputString(field.value)}
                    onChange={(e) => field.onChange(+e.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="loanTerm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Loan Term</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step={1}
                    {...field}
                    value={numToInputString(field.value)}
                    onChange={(e) => field.onChange(+e.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="interestOnly"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Interest Only</FormLabel>
                <FormControl>
                  <Switch
                    name={field.name}
                    disabled={field.disabled}
                    onBlur={field.onBlur}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="repaymentFrequency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Repayment Frequency</FormLabel>
                <FormControl>
                  <Select {...field} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      {FrequencyTypeArr.map((val) => (
                        <SelectItem value={val}>{val}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex-col items-start justify-items-center gap-1 text-md">
          <div className="text-muted-foreground">
            ${+(mortgageCostPerYear / 52).toFixed(2)} per week
          </div>
          <div className="text-muted-foreground">
            ${+(mortgageCostPerYear / 26).toFixed(2)} per fortnight
          </div>
          <div className="text-muted-foreground">
            ${+(mortgageCostPerYear / 12).toFixed(2)} per month
          </div>
        </div>
        <Button type="submit" className="w-full mb-2">
          Save
        </Button>
        <Button
          type="button"
          className="w-full"
          variant="secondary"
          onClick={onCancelHandler}
        >
          Cancel
        </Button>
      </form>
    </Form>
  );
}

interface DialogDrawerMortgageFormProps extends React.ComponentProps<"tr"> {
  data: MortgageDataSchema;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFormSubmit: (data: MortgageDataSchema) => void;
}

function DialogDrawerMortgageForm({
  data,
  onFormSubmit,
  open,
  onOpenChange,
}: DialogDrawerMortgageFormProps) {
  const handleFormSubmit = (data: MortgageDataSchema) => {
    onFormSubmit(data);
    onOpenChange(false);
  };

  return (
    <DialogDrawer
      title="Edit mortgage"
      description="Make changes to your mortgage here. Click save when you're done."
      onOpenChange={onOpenChange}
      open={open}
    >
      <MortgageForm
        initialValues={data}
        onFormSubmit={handleFormSubmit}
        onFormCancel={() => onOpenChange(false)}
      />
    </DialogDrawer>
  );
}

interface HousingSectionProps {
  mortgageData: MortgageDataSchema[];
  onCreate: CreateHandlerFlat<MortgageDataSchema>;
  onUpdate: UpdateHandlerFlat<MortgageDataSchema>;
  onDelete: DeleteHandlerFlat<MortgageDataSchema>;
}

export function HousingSection({
  mortgageData,
  onCreate,
  onUpdate,
  onDelete,
}: HousingSectionProps) {
  const rowSelection = useMemo(
    () =>
      mortgageData.reduce((acc, entry, idx) => {
        return {
          ...acc,
          [idx]: entry.selected,
        };
      }, {} as RowSelectionState),
    [mortgageData]
  );

  const onRowSelectionChange: OnChangeFn<RowSelectionState> = (
    rowSelectionUpdater
  ) => {
    const newRowSelection =
      typeof rowSelectionUpdater === "function"
        ? rowSelectionUpdater(rowSelection)
        : rowSelectionUpdater;
    const newData = mortgageData.map((entry, idx) => ({
      ...entry,
      selected: newRowSelection[idx] || false,
    }));
    onUpdate(newData);
  };

  const table = useEditableTable({
    columns: mortgageDatacolumns,
    data: mortgageData,
    onCreate: () => onCreate(defaultMortgageData()),
    onDelete: (row) => {
      const entry = mortgageData[row.index];
      if (entry) {
        onDelete(entry);
      }
    },
    rowSelection,
    onRowSelectionChange,
  });

  const totalMortgageCostPerYear = useMemo(() => {
    return mortgageData
      .map((data) => {
        if (data.selected) {
          const {
            loanAmount,
            interestRate,
            loanTerm,
            interestOnly,
            repaymentFrequency,
          } = data;
          const repayment = calculateMortgageCostPerYear({
            loanAmount,
            loanTerm,
            interestRate: interestRate / 100,
            interestOnly,
            repaymentFrequency,
          });
          return repayment;
        } else {
          return 0;
        }
      })
      .reduce((prev, current) => prev + current, 0);
  }, [mortgageData]);

  const [selectedRow, setSelectedRow] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  // TODO: Look at adding interest only option

  return (
    <div>
      <DialogDrawerMortgageForm
        onFormSubmit={(newData) => onUpdate(newData)}
        data={mortgageData[selectedRow] || defaultMortgageData()}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
      <SubSectionCard
        title={
          <div className="flex flex-row">
            <div className="grow">Mortgage</div>
            <ResetButton
              onClick={() => {
                onDelete(mortgageData);
                onCreate(initialMortgageData);
              }}
            />
          </div>
        }
        description="Housing"
      >
        <EditableDataTable
          table={table}
          onCellClick={(cell) => {
            setSelectedRow(cell.row.index);
            setDialogOpen(true);
          }}
        />
        <Button
          variant="ghost"
          className="h-8 w-full p-0"
          onClick={() => onCreate(defaultMortgageData())}
        >
          <span className="sr-only">Add</span>
          <Plus className="h-4 w-4" />
        </Button>
        <div className="flex-col items-start gap-1 text-md">
          <div className="text-muted-foreground">
            ${+(totalMortgageCostPerYear / 52).toFixed(2)} per week
          </div>
          <div className="text-muted-foreground">
            ${+(totalMortgageCostPerYear / 26).toFixed(2)} per fortnight
          </div>
          <div className="text-muted-foreground">
            ${+(totalMortgageCostPerYear / 12).toFixed(2)} per month
          </div>
        </div>
      </SubSectionCard>
      {/* TODO: Show chart of mortgage over 30 years */}
    </div>
  );
}
